import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle, Ban } from 'lucide-react';

export interface PackageGrantForm {
  orgId: string;
  categoryId: string;
  levelId: string;
  packageType: string;
  price: number;
  actualPrice: number;
  expireDate: string;
  customDays: number;
}

interface Organization {
  id: string;
  name: string;
}

interface Category {
  id: string;
  name: string;
}

interface Level {
  id: string;
  name: string;
}

interface PackageGrantModalProps {
  // 弹窗状态
  showPackageModal: boolean;
  setShowPackageModal: (show: boolean) => void;
  showConfirmPackageModal: boolean;
  setShowConfirmPackageModal: (show: boolean) => void;

  // 用户信息
  userName: string;
  userLabel?: string; // "学员姓名" 或 "教师姓名"

  // 表单数据
  packageForm: PackageGrantForm;
  setPackageForm: (form: PackageGrantForm | ((prev: PackageGrantForm) => PackageGrantForm)) => void;

  // 选项数据
  orgs: Organization[];
  availableCategories: Category[];
  availableLevels: Level[];

  // 回调函数
  handleOrgChange: (orgId: string) => void;
  handleCategoryChange: (catId: string) => void;
  handleLevelChange: (levelId: string) => void;
  onPackageTypeChange?: (packageType: string) => void; // 套餐类型切换回调

  // 校验状态
  isChecking: boolean;
  hasPaidPapers: boolean | null;
  validationError: string;

  // 确认开通回调
  onConfirm: () => void;
}

const PACKAGE_TYPES = [
  { value: 'month', label: '月包' },
  { value: 'quarter', label: '季包' },
  { value: 'halfYear', label: '半年包' },
  { value: 'year', label: '年包' },
  { value: 'custom', label: '自定义包' },
];

export default function PackageGrantModal({
  showPackageModal,
  setShowPackageModal,
  showConfirmPackageModal,
  setShowConfirmPackageModal,
  userName,
  userLabel = '学员姓名',
  packageForm,
  setPackageForm,
  orgs,
  availableCategories,
  availableLevels,
  handleOrgChange,
  handleCategoryChange,
  handleLevelChange,
  onPackageTypeChange,
  isChecking,
  hasPaidPapers,
  validationError,
  onConfirm,
}: PackageGrantModalProps) {
  const canSubmit = packageForm.orgId && packageForm.categoryId && packageForm.levelId && packageForm.packageType && hasPaidPapers === true;

  return (
    <>
      {/* 主弹窗 */}
      <AnimatePresence>
        {showPackageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl p-6 w-[500px] max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-4">开通刷题包 - {userName}</h3>
              <div className="space-y-4">
                {/* 考评体系 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">考评体系</label>
                  <select
                    value={packageForm.orgId}
                    onChange={e => handleOrgChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="">请选择考评体系</option>
                    {orgs.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>

                {/* 课程分类 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">课程分类</label>
                  <select
                    value={packageForm.categoryId}
                    onChange={e => handleCategoryChange(e.target.value)}
                    disabled={!packageForm.orgId}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm disabled:bg-gray-100"
                  >
                    <option value="">请选择课程分类</option>
                    {availableCategories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* 级别分类 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">级别分类</label>
                  <select
                    value={packageForm.levelId}
                    onChange={e => handleLevelChange(e.target.value)}
                    disabled={!packageForm.categoryId}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm disabled:bg-gray-100"
                  >
                    <option value="">请选择级别</option>
                    {availableLevels.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>

                {/* 套餐类型 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">套餐类型</label>
                  <select
                    value={packageForm.packageType}
                    onChange={e => {
                      const packageType = e.target.value;
                      // 调用父组件的回调函数处理套餐类型切换（包括自动计算到期日期）
                      if (onPackageTypeChange) {
                        onPackageTypeChange(packageType);
                      } else {
                        // 降级方案：只更新套餐类型
                        setPackageForm(prev => ({ ...prev, packageType }));
                      }
                    }}
                    disabled={!packageForm.levelId}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm disabled:bg-gray-100"
                  >
                    <option value="">请选择</option>
                    {PACKAGE_TYPES.map(pt => (
                      <option key={pt.value} value={pt.value}>{pt.label}</option>
                    ))}
                  </select>
                </div>

                {/* 价格显示 */}
                {packageForm.price > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">价格：</span>¥{packageForm.price}
                    </p>
                  </div>
                )}

                {/* 实付金额提示 */}
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    <span className="font-medium">实付金额：</span>¥0（后台开通，无实际支付）
                  </p>
                </div>

                {/* 到期时间 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">到期时间</label>
                  {packageForm.packageType === 'custom' ? (
                    <>
                      <div className="relative">
                        <input
                          type="number"
                          min={1}
                          max={3650}
                          value={packageForm.customDays}
                          onChange={e => {
                            const days = Math.max(1, Math.min(3650, Number(e.target.value) || 1));
                            // customDays由父组件管理
                            setPackageForm(prev => ({ ...prev, customDays: days }));
                          }}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="请输入天数（1-3650）"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">天</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">预计到期：{packageForm.expireDate}</p>
                    </>
                  ) : (
                    <>
                      <input
                        type="date"
                        value={packageForm.expireDate}
                        disabled
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">根据套餐类型自动计算</p>
                    </>
                  )}
                </div>

                {/* 校验状态 */}
                {isChecking && (
                  <div className="flex items-center gap-2 text-blue-600 bg-blue-50 p-3 rounded-lg">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">正在校验试卷...</span>
                  </div>
                )}
                {!isChecking && hasPaidPapers === true && (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">校验通过，可开通</span>
                  </div>
                )}
                {!isChecking && hasPaidPapers === false && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                    <Ban className="w-4 h-4" />
                    <span className="text-sm">{validationError}</span>
                  </div>
                )}
              </div>

              {/* 按钮 */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowPackageModal(false)}
                  className="flex-1 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  取消
                </button>
                <button
                  onClick={() => setShowConfirmPackageModal(true)}
                  disabled={!canSubmit}
                  className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  确认开通
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* 确认弹窗 */}
      <AnimatePresence>
        {showConfirmPackageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl p-6 w-[480px]"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-4">确认开通套餐</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{userLabel}</span>
                  <span className="text-sm font-medium text-gray-800">{userName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">考评体系</span>
                  <span className="text-sm font-medium text-gray-800">
                    {orgs.find(o => o.id === packageForm.orgId)?.name || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">课程分类</span>
                  <span className="text-sm font-medium text-gray-800">
                    {availableCategories.find(c => c.id === packageForm.categoryId)?.name || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">级别分类</span>
                  <span className="text-sm font-medium text-gray-800">
                    {availableLevels.find(l => l.id === packageForm.levelId)?.name || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">套餐类型</span>
                  <span className="text-sm font-medium text-gray-800">
                    {PACKAGE_TYPES.find(pt => pt.value === packageForm.packageType)?.label || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">到期时间</span>
                  <span className="text-sm font-medium text-gray-800">{packageForm.expireDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">支付方式</span>
                  <span className="text-sm font-medium text-yellow-600">后台开通（无实际支付）</span>
                </div>
              </div>
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mb-4">
                请确认以上信息无误，开通后将立即生效。
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmPackageModal(false)}
                  className="flex-1 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  返回修改
                </button>
                <button
                  onClick={onConfirm}
                  className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                >
                  确认开通
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
